import axios from 'axios'
import React, { useEffect, useState } from 'react';
import Title from '../../../components/Title';

import Typography from '@mui/material/Typography';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import RoleModal from './modal/RoleModal';
import { CardContent, Checkbox, CircularProgress, Divider, FormControlLabel, Stack } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Role } from '../../../models/role';
import Button from '@mui/material/Button';
import useAxiosInterceptor from '../../AxiosInterceptor';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { Card } from '@mui/material';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useDrawer } from '../../../components/context/DrawerContext';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}
interface PermissionItem {
  id: number;
  name: string;
  action: string;
  checked: boolean;
}

interface MenuItem {
  id: number;
  name: string;
  permissions: PermissionItem[];
}

interface ModuleItem {
  id: number;
  name: string;
  menus: MenuItem[];
}

const dedupePermissions = <T extends { action?: string; name?: string }>(permissions: T[] = []) => {
  const seenKeys = new Set<string>();

  return permissions.filter((permission) => {
    const actionKey = String(permission.action || '').toLowerCase().trim();
    const nameKey = String(permission.name || '').toLowerCase().trim();
    const dedupeKey = actionKey || nameKey;

    if (!dedupeKey || seenKeys.has(dedupeKey)) {
      return false;
    }

    seenKeys.add(dedupeKey);
    return true;
  });
};

const Privilegios: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {

  useAxiosInterceptor();
  document.title = "Privilegios";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [checkedItems, setCheckedItems] = useState('');
  const [perfil, setPerfil] = React.useState('');
  const [roles, setRoles] = useState([]);
  const [role_id, setRole_id] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/privilegios');
  const canManagePrivileges = canUpdate || canCreate;
  const canManageMenus = canCreate || canUpdate || canDelete;
  const { drawerOpen } = useDrawer();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingRoles(true);
      try {
        const [rolesResponse] = await Promise.all([
          axios.get('roles')
        ]);
        setRoles(rolesResponse.data);
      } catch (error) {
        showAlert('Error fetching data!', 'error');
      } finally {
        setIsLoadingRoles(false);
      }
    };
    fetchData();
  }, [showAlert]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingPermissions(true);
      try {
        const response = await axios.get(`permissions/${role_id}`);
        const sanitizedModules = (response.data || []).map((module: ModuleItem) => ({
          ...module,
          menus: (module.menus || []).map((menu) => ({
            ...menu,
            permissions: dedupePermissions(menu.permissions || []),
          })),
        }));
        setModules(sanitizedModules);
      } catch (error) {
        showAlert('Error fetching permissions!', 'error');
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    if (role_id) fetchData();
  }, [role_id, showAlert]);

  const handleCheckboxChange = (permissionId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedModules = modules.map(module => ({
      ...module,
      menus: module.menus.map(menu => ({
        ...menu,
        permissions: menu.permissions.map(permission =>
          permission.id === permissionId
            ? { ...permission, checked: event.target.checked }
            : permission
        )
      }))
    }));
    setModules(updatedModules);
  };
  const handleSavePermissions = async () => {
    if (!canManagePrivileges) return;

    const selectedPermissions = modules
      .flatMap(module => module.menus)
      .flatMap(menu => menu.permissions)
      .filter(permission => permission.checked)
      .map(permission => permission.id);

    try {
      showLoading();
      await axios.post(`administrar/permissions/${role_id}`, {
        permission_ids: selectedPermissions
      });
      showAlert("Permisos guardados exitosamente", "success");
    } catch (error) {
      console.error(error);
      showAlert("Error al guardar permisos", "error");
    } finally {
      hideLoading();
    }
  };

  const handleModuleToggle = (moduleId: number, checked: boolean) => {
    const updatedModules = modules.map(module => {
      if (module.id !== moduleId) return module;

      return {
        ...module,
        menus: module.menus.map(menu => ({
          ...menu,
          permissions: menu.permissions.map(permission => ({
            ...permission,
            checked
          }))
        }))
      };
    });

    setModules(updatedModules);
  };
  const refreshRoles = async () => {
    try {
      const rolesResponse = await axios.get("roles");
      setRoles(rolesResponse.data);
    } catch {
      showAlert("Error al refrescar los perfiles", "error");
    }
  };
  return (
    <HeaderResponsive>
      <StickyListToolbar
        sx={{
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
        }}
      >
        <Title>Perfiles</Title>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
          alignItems="stretch"
          justifyContent="flex-end"
        >
          <PermissionButton
            allowed={canManageMenus}
            deniedTooltip="Sin permiso para administrar menú"
            tooltip="Administrar menú"
            variant="outlined"
            size="small"
            component={NavLink}
            to="/administrar/privilegios/add"
            startIcon={isMobile ? null : <SettingsApplicationsIcon />}
            fullWidth={isMobile}
          >
            {isMobile ? <SettingsApplicationsIcon /> : "Administrar menú"}
          </PermissionButton>
          <PermissionButton
            allowed={canCreate}
            deniedTooltip="Sin permiso para crear"
            tooltip="Agregar Perfil"
            variant="contained"
            size="small"
            startIcon={isMobile ? null : <PersonAddIcon />}
            onClick={() => setOpenModal(true)}
            fullWidth={isMobile}
          >
            {isMobile ? <PersonAddIcon /> : "Agregar Perfil"}
          </PermissionButton>
          <PermissionButton
            allowed={canManagePrivileges}
            deniedTooltip="Sin permiso para editar"
            tooltip="Guardar"
            variant="contained"
            size="small"
            onClick={handleSavePermissions}
            fullWidth={isMobile}
            sx={{ backgroundColor: 'black' }}
          >
            {isMobile ? <AddIcon /> : "Guardar"}
          </PermissionButton>
        </Stack>
      </StickyListToolbar>
      <Card
        sx={{
          width: (theme) => stickyListRegionWidth(theme, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <CardContent>
          <Grid item xs={12} sm={6} xl={6}>
            <FormControl fullWidth>
              <InputLabel id="collect">Seleccionar Perfil</InputLabel>
              {!isLoadingRoles && roles && roles.length > 0 ? (
                <Select
                  label="Seleccionar Perfil"
                  value={role_id} // Use the state variable heres
                  size='small'
                  id="role_id"
                  labelId="role_id"
                  onChange={(event) => setRole_id(event.target.value)}
                  required
                >
                  {roles.map((role: Role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : null}
            </FormControl>
          </Grid>
          <Divider sx={{ my: 2 }} />
          {isLoadingRoles || isLoadingPermissions ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={30} />
            </Box>
          ) : modules.length > 0 ? (
            <Box display="flex" flexDirection="column" minWidth={0}>
              {modules.map((module) => (
                <CardContent
                  key={module.id}
                  sx={{
                    border: '1px solid #1976d2',
                    borderRadius: 2,
                    mb: 3,
                    my: 2,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {module.name}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={module.menus.every(menu =>
                            menu.permissions.every(p => p.checked)
                          )}
                          onChange={(e) => handleModuleToggle(module.id, e.target.checked)}
                        />
                      }
                      label="Seleccionar todos"
                    />
                  </Box>

                  <Grid container spacing={1}>
                    {module.menus.map((menu) => (
                      <Grid item xs={12} sm={6} md={2} key={menu.id}>
                        <Box>
                          <Typography fontWeight="bold" mb={1}>
                            {menu.name}
                          </Typography>

                          {/* Ordenar toggle_visibility primero */}
                          {[
                            ...menu.permissions.filter((p) => p.action === 'toggle_visibility'),
                            ...menu.permissions.filter((p) => p.action !== 'toggle_visibility'),
                          ].map((permission) => (
                            <Box key={permission.id}>

                              <FormControlLabel
                                key={permission.id}
                                control={
                                  <Checkbox
                                    checked={permission.checked}
                                    onChange={handleCheckboxChange(permission.id)}
                                  />
                                }
                                label={permission.name}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              ))}

            </Box>
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <RoleModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        roles={roles}
        canCreate={canCreate}
        canDelete={canDelete}
        refreshRoles={refreshRoles}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />

    </HeaderResponsive>

  )
}

export default Privilegios
